import React, { useState, useCallback, useEffect, useRef } from 'react';
import ApiKeyStep from './steps/ApiKeyStep';
import CompanyInfoStep from './steps/CompanyInfoStep';
import ContractStep from './steps/ContractStep';
import TwilioNumberStep from './steps/TwilioNumberStep';
import ConfirmationStep from './steps/ConfirmationStep';
import { OnboardingFormData, OnboardingStep } from '../types';
import Logo from './Logo';
import UserMenu from './UserMenu';
import TopBar from './TopBar';
import { cn } from "@/lib/utils";
import { Check, Loader2, Pencil, ArrowLeft } from "lucide-react";
import authService from '../services/authService';
import contractService from '../services/contractService';
import twilioService from '../services/twilioService';
import voiceService from '../services/voiceService';
import whatsappService from '../services/whatsappService';

const OnboardingForm: React.FC = () => {
  const [activeStep, setActiveStep] = useState<OnboardingStep>(OnboardingStep.API_KEY);
  const [secretClicks, setSecretClicks] = useState<number[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [highestCompletedIndex, setHighestCompletedIndex] = useState(-1);
  const [viewMode, setViewMode] = useState<'overview' | 'edit'>('edit');
  const [completedSteps, setCompletedSteps] = useState<boolean[]>([false, false, false, false, false]);
  const [phoneAgentNumber, setPhoneAgentNumber] = useState<string | undefined>(undefined);
  const [whatsappAgentNumber, setWhatsappAgentNumber] = useState<string | undefined>(undefined);
  const progressChecked = useRef(false);
  const [formData, setFormData] = useState<OnboardingFormData>({
    apiKey: '',
    companyName: '',
    street: '',
    city: '',
    zipCode: '',
    country: '',
    contactPerson: '',
    email: '',
    phone: '',
    vatId: '',
    privacyPolicyUrl: '',
    hasAcceptedTerms: false,
    signature: null,
    dataProcessingSignature: null,
    directDebitSignature: null,
    currentSignatureStep: 0,
    payerServiceProviderName: '',
    payerIban: '',
    payerBic: '',
    payerCity: ''
  });

  // Check onboarding progress on mount and jump to the first incomplete step
  useEffect(() => {
    if (progressChecked.current) return;
    progressChecked.current = true;

    const checkProgress = async () => {
      try {
        // Fetch all relevant data in parallel
        const [
          timeglobeKeyResult,
          businessInfoResult,
          contractConfirmResult,
          avvConfirmResult,
          mandatResult,
          twilioNumberResult,
          voiceSettingsResult,
          whatsappResult,
        ] = await Promise.allSettled([
          authService.getBusinessTimeglobeKey(),
          authService.getBusinessInfo(),
          contractService.getContractConfirmation(),
          contractService.getDataProcessingConfirmation(),
          contractService.getLastschriftmandat(),
          twilioService.getLatestNumber(),
          voiceService.getSettings().catch(() => ({ privacy_policy_url: '' })),
          // The logged-in salon's own WhatsApp line (login token). It used to
          // be read by e-mail without a login (/whatsapp/status-public).
          whatsappService.getConnectivity(),
        ]);

        // Extract results (null if failed)
        const keyData = timeglobeKeyResult.status === 'fulfilled' ? timeglobeKeyResult.value : null;
        const bizData = businessInfoResult.status === 'fulfilled' ? businessInfoResult.value : null;
        const contractConfirm = contractConfirmResult.status === 'fulfilled' ? contractConfirmResult.value as any : null;
        const avvConfirm = avvConfirmResult.status === 'fulfilled' ? avvConfirmResult.value as any : null;
        const mandat = mandatResult.status === 'fulfilled' ? mandatResult.value as any : null;
        const twilioNum = twilioNumberResult.status === 'fulfilled' ? twilioNumberResult.value as any : null;
        const voiceSettings = voiceSettingsResult.status === 'fulfilled' ? voiceSettingsResult.value : null;

        // Pre-populate formData from fetched results
        const updates: Partial<OnboardingFormData> = {};

        // Step 1: API Key
        const hasApiKey = !!(keyData?.timeglobe_auth_key);
        if (hasApiKey) {
          updates.apiKey = keyData.timeglobe_auth_key;
        }

        // Step 2: Company Info
        if (bizData) {
          updates.companyName = bizData.business_name || '';
          updates.vatId = bizData.tax_id || '';
          updates.street = bizData.street_address || '';
          updates.zipCode = bizData.postal_code || '';
          updates.city = bizData.city || '';
          updates.country = bizData.country || '';
          updates.contactPerson = bizData.contact_person || '';
          updates.email = bizData.email || '';
          updates.phone = bizData.phone_number || '';
        }
        if (voiceSettings?.privacy_policy_url) {
          updates.privacyPolicyUrl = voiceSettings.privacy_policy_url;
        }
        const hasCompanyInfo = !!(
          updates.companyName && updates.street && updates.city &&
          updates.zipCode && updates.country && updates.contactPerson &&
          updates.email && updates.phone && updates.vatId
        );

        // Step 3: Contract (3 sub-steps: main contract, AVV, lastschriftmandat)
        const mainContractConfirmed = !!(contractConfirm?.confirmed);
        const avvConfirmed = !!(avvConfirm?.confirmed);
        const mandatExists = !!mandat;

        if (mainContractConfirmed) {
          updates.hasAcceptedTerms = true;
        }
        if (mandatExists) {
          updates.directDebitSignature = 'existing-file';
          updates.payerServiceProviderName = mandat.payer_name || '';
          updates.payerIban = mandat.payer_iban || '';
          updates.payerBic = mandat.payer_bic || '';
          updates.payerCity = mandat.payer_city || '';
        }
        // If all 3 contract sub-steps are done, set currentSignatureStep to last
        if (mainContractConfirmed && avvConfirmed && mandatExists) {
          updates.currentSignatureStep = 2;
        } else if (mainContractConfirmed && avvConfirmed) {
          updates.currentSignatureStep = 2;
        } else if (mainContractConfirmed) {
          updates.currentSignatureStep = 1;
        }
        const hasAllContracts = mainContractConfirmed && avvConfirmed && mandatExists;

        // Step 5: WhatsApp connection (best-effort: unreadable = not connected)
        const waStatus = whatsappResult.status === 'fulfilled' ? whatsappResult.value : null;
        const whatsappConnected = !!waStatus?.is_connected;
        const whatsappNumber: string | undefined = waStatus?.whatsapp_number || undefined;

        // Step 4: Twilio Number + Agent preferences
        const hasTwilioNumber = !!(twilioNum?.phone_number);
        if (hasTwilioNumber) {
          updates.twilioPurchasedNumber = twilioNum.phone_number;
          updates.wantsTwilioNumber = true;
        }

        // Restore agent preferences from business info
        if (bizData?.wants_phone_agent !== undefined && bizData?.wants_phone_agent !== null) {
          updates.wantsPhoneAgent = bizData.wants_phone_agent;
        }
        if (bizData?.wants_whatsapp !== undefined && bizData?.wants_whatsapp !== null) {
          updates.wantsWhatsApp = bizData.wants_whatsapp;
        }
        if (bizData?.whatsapp_use_reserved_number !== undefined && bizData?.whatsapp_use_reserved_number !== null) {
          updates.whatsappUseReservedNumber = bizData.whatsapp_use_reserved_number;
          updates.hasOwnWhatsappNumber = !bizData.whatsapp_use_reserved_number;
        }

        // If WhatsApp is actually connected, force the two questions to a consistent state
        if (whatsappConnected) {
          updates.wantsWhatsApp = true;
          if (updates.whatsappUseReservedNumber === undefined) {
            const twilioNumber = twilioNum?.phone_number;
            if (whatsappNumber && twilioNumber) {
              updates.whatsappUseReservedNumber = whatsappNumber === twilioNumber;
              updates.hasOwnWhatsappNumber = whatsappNumber !== twilioNumber;
            } else {
              updates.whatsappUseReservedNumber = true;
              updates.hasOwnWhatsappNumber = false;
            }
          }
        }

        // Apply all updates at once
        setFormData(prev => ({ ...prev, ...updates }));

        // Determine the first incomplete step
        let targetStepIndex = 0;
        if (hasApiKey) targetStepIndex = 1;
        if (hasApiKey && hasCompanyInfo) targetStepIndex = 2;
        if (hasApiKey && hasCompanyInfo && hasAllContracts) targetStepIndex = 3;
        if (hasApiKey && hasCompanyInfo && hasAllContracts && hasTwilioNumber) targetStepIndex = 4;

        setHighestCompletedIndex(targetStepIndex - 1);
        setActiveStep(getStepFromIndex(targetStepIndex));

        const whatsappStepComplete = whatsappConnected || bizData?.wants_whatsapp === false;
        const stepCompletion = [
          hasApiKey,
          hasCompanyInfo,
          hasAllContracts,
          hasTwilioNumber,
          whatsappStepComplete,
        ];
        setCompletedSteps(stepCompletion);
        setPhoneAgentNumber(twilioNum?.phone_number || undefined);
        setWhatsappAgentNumber(whatsappConnected ? (whatsappNumber || undefined) : undefined);
        const fullyOnboarded = hasApiKey && hasCompanyInfo && hasAllContracts && hasTwilioNumber;
        if (fullyOnboarded) {
          setViewMode('overview');
        }
      } catch (err) {
        console.error('Error checking onboarding progress:', err);
        // On error, start from step 1
      } finally {
        setIsInitialLoading(false);
      }
    };

    checkProgress();
  }, []);

  const getNextStep = (currentStep: OnboardingStep): OnboardingStep => {
    switch(currentStep) {
      case OnboardingStep.API_KEY: return OnboardingStep.COMPANY_INFO;
      case OnboardingStep.COMPANY_INFO: return OnboardingStep.CONTRACT;
      case OnboardingStep.CONTRACT: return OnboardingStep.TWILIO_NUMBER;
      case OnboardingStep.TWILIO_NUMBER: return OnboardingStep.CONFIRMATION;
      default: return OnboardingStep.CONFIRMATION;
    }
  };

  const getPreviousStep = (currentStep: OnboardingStep): OnboardingStep => {
    switch(currentStep) {
      case OnboardingStep.CONFIRMATION: return OnboardingStep.TWILIO_NUMBER;
      case OnboardingStep.TWILIO_NUMBER: return OnboardingStep.CONTRACT;
      case OnboardingStep.CONTRACT: return OnboardingStep.COMPANY_INFO;
      case OnboardingStep.COMPANY_INFO: return OnboardingStep.API_KEY;
      default: return OnboardingStep.API_KEY;
    }
  };

  const getStepIndex = (step: OnboardingStep): number => {
    switch(step) {
      case OnboardingStep.API_KEY: return 0;
      case OnboardingStep.COMPANY_INFO: return 1;
      case OnboardingStep.CONTRACT: return 2;
      case OnboardingStep.TWILIO_NUMBER: return 3;
      case OnboardingStep.CONFIRMATION: return 4;
      default: return 0;
    }
  };

  const getStepFromIndex = (index: number): OnboardingStep => {
    switch(index) {
      case 0: return OnboardingStep.API_KEY;
      case 1: return OnboardingStep.COMPANY_INFO;
      case 2: return OnboardingStep.CONTRACT;
      case 3: return OnboardingStep.TWILIO_NUMBER;
      case 4: return OnboardingStep.CONFIRMATION;
      default: return OnboardingStep.API_KEY;
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex === 4) {
      const now = Date.now();
      const recent = [...secretClicks, now].filter((t) => now - t < 1200);
      setSecretClicks(recent);
      if (recent.length >= 3) {
        setSecretClicks([]);
        setActiveStep(getNextStep(activeStep));
        return;
      }
    }

    const currentStepIndex = getStepIndex(activeStep);
    // Allow navigation to any previously completed step, or one step forward
    if (stepIndex <= currentStepIndex || stepIndex === currentStepIndex + 1 || stepIndex <= highestCompletedIndex + 1) {
      setActiveStep(getStepFromIndex(stepIndex));
    }
  };

  const handleNext = () => {
    const currentIdx = getStepIndex(activeStep);
    if (currentIdx > highestCompletedIndex) {
      setHighestCompletedIndex(currentIdx);
    }
    setActiveStep(getNextStep(activeStep));
  };
  const handleBack = () => setActiveStep(getPreviousStep(activeStep));

  const handleFormChange = useCallback((data: Partial<OnboardingFormData>) => {
    setFormData((prevData) => ({ ...prevData, ...data }));
  }, []);

  const steps = ['API-Schlüssel', 'Unternehmen', 'Vertrag', 'Telefon-Agent', 'WhatsApp-Agent'];
  const stepDescriptions = [
    'TimeGlobe API-Zugang eingerichtet',
    'Unternehmensdaten hinterlegt',
    'Verträge unterschrieben',
    'Telefon-Agent konfiguriert',
    'WhatsApp-Agent verbunden',
  ];
  const currentStepIndex = getStepIndex(activeStep);

  const handleOverviewStepClick = (stepIndex: number) => {
    setActiveStep(getStepFromIndex(stepIndex));
    setViewMode('edit');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case OnboardingStep.API_KEY:
        return <ApiKeyStep apiKey={formData.apiKey} onFormChange={handleFormChange} onNext={handleNext} />;
      case OnboardingStep.COMPANY_INFO:
        return <CompanyInfoStep formData={formData} onFormChange={handleFormChange} onNext={handleNext} onBack={handleBack} />;
      case OnboardingStep.CONTRACT:
        return <ContractStep formData={formData} onFormChange={handleFormChange} onNext={handleNext} onBack={handleBack} />;
      case OnboardingStep.TWILIO_NUMBER:
        return <TwilioNumberStep formData={formData} onFormChange={handleFormChange} onNext={handleNext} onBack={handleBack} />;
      case OnboardingStep.CONFIRMATION:
        return <ConfirmationStep formData={formData} onFormChange={handleFormChange} onBack={handleBack} />;
      default:
        return null;
    }
  };

  if (isInitialLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <TopBar />
        <header className="bg-card border-b border-border">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <div className="scale-90 origin-left">
              <Logo />
            </div>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-10 w-10 animate-spin text-[#1967D2]" />
            <p className="text-muted-foreground text-sm">Onboarding-Fortschritt wird geladen...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background pb-12">
      <TopBar />

      <header className="bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="scale-90 origin-left">
            <Logo />
          </div>
          <UserMenu formData={formData} />
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-5xl flex-1">
        <h1 className="text-2xl md:text-3xl font-medium text-center text-foreground mb-8 md:mb-12">
          WhatsApp Termin Assistent Onboarding
        </h1>

        {viewMode === 'overview' ? (
          <div>
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-success/10 border border-success/25 text-success-strong text-sm font-medium mb-4">
                <Check className="h-4 w-4" />
                Onboarding abgeschlossen
              </div>
              <p className="text-muted-foreground text-sm md:text-base max-w-2xl mx-auto">
                Ihr Assistent ist eingerichtet. Klicken Sie auf einen Punkt, um die entsprechenden Angaben zu bearbeiten.
              </p>
            </div>
            <div className="grid gap-3 md:gap-4">
              {steps.map((label, index) => {
                const done = completedSteps[index];
                const isLiveStep = index === 3 || index === 4;
                const liveNumber = index === 3 ? phoneAgentNumber : index === 4 ? whatsappAgentNumber : undefined;
                const badgeLabel = done && isLiveStep ? 'Live' : done ? 'Abgeschlossen' : null;
                const badgeClass = done && isLiveStep
                  ? 'text-success-strong bg-success/10 border-success/25'
                  : 'text-success-strong bg-success/10 border-success/25';
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => handleOverviewStepClick(index)}
                    className="group flex items-center gap-4 bg-card border border-border rounded-xl px-4 md:px-6 py-4 text-left hover:border-[#1967D2] hover:shadow-sm transition-all"
                  >
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center shrink-0 border-2",
                      done ? "bg-[#1967D2] border-[#1967D2] text-white" : "bg-card border-border text-muted-foreground"
                    )}>
                      {done ? <Check className="h-5 w-5" /> : index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground">{label}</span>
                        {badgeLabel && (
                          <span className={cn(
                            "inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2 py-0.5 border",
                            badgeClass
                          )}>
                            {done && isLiveStep && (
                              <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-75 animate-ping" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                              </span>
                            )}
                            {badgeLabel}
                          </span>
                        )}
                        {done && isLiveStep && liveNumber && (
                          <span className="text-sm font-mono text-muted-foreground">{liveNumber}</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{stepDescriptions[index]}</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground group-hover:text-[#1967D2] transition-colors shrink-0">
                      <Pencil className="h-4 w-4" />
                      <span className="hidden sm:inline">Bearbeiten</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            {completedSteps.slice(0, 4).every(Boolean) && (
              <div className="mb-6">
                <button
                  type="button"
                  onClick={() => setViewMode('overview')}
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#1967D2] transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Zur Übersicht
                </button>
              </div>
            )}

        {/* Stepper */}
        <div className="hidden md:flex justify-between items-center mb-12 relative px-12">
          {/* Connecting Line Background */}
          <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-muted -z-0 mx-20" />

          {/* Active Line Progress */}
          <div
             className="absolute left-0 top-1/2 -translate-y-1/2 h-[2px] bg-[#1967D2] -z-0 mx-20 transition-all duration-300 ease-in-out"
             style={{ width: `calc(${currentStepIndex / (steps.length - 1)} * (100% - 10rem))` }}
          />

          {steps.map((label, index) => {
            const isCompleted = index < currentStepIndex || index <= highestCompletedIndex;
            const isActive = index === currentStepIndex;
            const isClickable = index <= currentStepIndex || index === currentStepIndex + 1 || index <= highestCompletedIndex + 1;

            return (
              <div key={label} className={cn("relative z-10 flex flex-col items-center group", isClickable ? "cursor-pointer" : "cursor-default")} onClick={() => handleStepClick(index)}>
                <div
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 border-2",
                    isActive
                      ? "bg-[#1967D2] border-[#1967D2] text-white shadow-lg shadow-blue-500/30 scale-110"
                      : isCompleted
                        ? "bg-[#1967D2] border-[#1967D2] text-white"
                        : "bg-card border-border text-muted-foreground group-hover:border-[#1967D2] group-hover:text-[#1967D2]"
                  )}
                >
                  {isCompleted && !isActive ? <Check className="h-5 w-5" /> : index + 1}
                </div>
                <span className={cn(
                  "absolute top-14 text-xs md:text-sm whitespace-nowrap font-medium transition-colors duration-200",
                  isActive ? "text-[#1967D2]" : isCompleted ? "text-foreground" : "text-muted-foreground"
                )}>
                  {label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Mobile Stepper Indicator */}
        <div className="md:hidden mb-6 flex items-center justify-between px-2">
           <span className="text-sm font-medium text-muted-foreground">Schritt {currentStepIndex + 1} von {steps.length}</span>
           <span className="text-sm font-semibold text-[#1967D2]">{steps[currentStepIndex]}</span>
        </div>
        <div className="md:hidden w-full h-1.5 bg-muted rounded-full mb-8 overflow-hidden">
           <div
             className="h-full bg-[#1967D2] transition-all duration-300"
             style={{ width: `${((currentStepIndex + 1) / steps.length) * 100}%` }}
           />
        </div>

        {/* Step Content */}
        <div className="bg-card rounded-xl shadow-sm border border-border p-4 md:p-8 transition-all duration-300 ease-in-out">
          {renderStepContent()}
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OnboardingForm;
